import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  panePath, paneAt, WINDOW_PANE, worthRecording, readyToRestore,
  landingPosition, trim,
} from "@/utils/scrollPanes";

/**
 * ScrollMemory (v16.05, rebuilt v18.61) - scroll position restoration.
 *
 * BrowserRouter swaps pages without any scroll handling, so navigating into a
 * screen and coming back landed you at the top.
 *
 * Behaviour, keyed by history entry (location.key):
 *  - Every scroll is recorded against the current entry.
 *  - Arriving at an entry seen before (back / forward: in-app navigate(-1),
 *    the browser button, the iOS swipe) restores the recorded position.
 *    Pages fetch after mount and grow over several frames, so restoration
 *    retries on rAF until the pane is tall enough (~1.5s cap, then it settles
 *    for what is available). A user scroll during the retry cancels it.
 *  - Arriving at a brand-new entry scrolls to the top.
 *
 * WHAT THIS HEADER USED TO SAY, AND WHY IT IS WORTH RECORDING
 *
 *     "The whole app scrolls the window, so only window scroll is tracked."
 *
 * That sentence stood for eighteen versions and was false of the busiest
 * screen in the app. ListDetailPage has a root of `fixed inset-0 flex
 * flex-col` and puts its whole body in a `flex-1 overflow-y-auto` child.
 * Inside a page shaped like that `window.scrollY` is 0 and stays 0, so this
 * component recorded 0, restored 0, and reported nothing. Scroll to the
 * fortieth item on a shop, open a price, come back: top of the list. Every
 * time.
 *
 * It worked on the short pages nobody complained about.
 *
 * Found by the Tradie's Bay chat, who hit the same thing in their fork and
 * asked the one question nobody here had: how many screens scroll inside a
 * container rather than the window? `grep -rl overflow-y-auto src/pages`
 * answered it in a second. Eleven.
 *
 * HOW PANES ARE FOUND, rather than listed: a `scroll` event does not bubble,
 * but it IS dispatched through the capture phase, so one listener on
 * `document` with capture:true sees every scrolling element in the app. A
 * list of container selectors would be the enumerate-the-spellings mistake
 * one more time - it would be right about the containers that existed on the
 * day it was written.
 *
 * WHAT IS DELIBERATELY NOT PORTED: Tradie's Bay also added a guard that
 * reverts any scroll not preceded by a touch or keypress, which is their fix
 * for a text input at the bottom of a container dragging the page down on
 * focus. This app already handles that case (data-scroll-allow, and
 * ListDetailPage's own focus nudge), and a guard that fights the app it
 * protects is worse than the bug. Not copied without the symptom.
 */

// location.key -> Map(pane path -> scrollTop)
const positions = new Map();
const MAX_ENTRIES = 80;

// Pages that already run their own scroll save/restore (listsScrollPos,
// mpMem, templates' inner container). ScrollMemory stays hands-off there so
// two restorers never race; their existing behaviour is unchanged.
const SELF_MANAGED = ["/", "/meal-planning", "/templates"]; // "/" = ListsPage
const selfManaged = (pathname) => SELF_MANAGED.some((p) => pathname === p);

function paneOf(target) {
  if (!target || target === document || target === document.documentElement) {
    return WINDOW_PANE;
  }
  return panePath(target, document.body);
}

function readTop(pane) {
  if (pane === WINDOW_PANE) return window.scrollY;
  const el = paneAt(pane, document.body);
  return el ? el.scrollTop : null;
}

function maxScrollOf(pane) {
  if (pane === WINDOW_PANE) {
    return document.documentElement.scrollHeight - window.innerHeight;
  }
  const el = paneAt(pane, document.body);
  return el ? el.scrollHeight - el.clientHeight : 0;
}

function scrollPaneTo(pane, top) {
  if (pane === WINDOW_PANE) {
    window.scrollTo(0, top);
    return;
  }
  const el = paneAt(pane, document.body);
  if (el) el.scrollTop = top;
}

export default function ScrollMemory() {
  const location = useLocation();
  const keyRef = useRef(location.key);

  // Record every scroll, wherever it happens. CAPTURE phase: scroll does not
  // bubble, so a listener on document only sees container scrolls this way.
  useEffect(() => {
    const onScroll = (ev) => {
      const pane = paneOf(ev.target);
      if (!pane) return;                       // detached mid-scroll
      const top = pane === WINDOW_PANE ? window.scrollY : ev.target.scrollTop;
      if (!worthRecording(top)) return;
      let forKey = positions.get(keyRef.current);
      if (!forKey) {
        forKey = new Map();
        positions.set(keyRef.current, forKey);
      }
      forKey.set(pane, top);
      trim(positions, MAX_ENTRIES);
    };
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => document.removeEventListener("scroll", onScroll, { capture: true });
  }, []);

  useEffect(() => {
    keyRef.current = location.key;
    if (selfManaged(location.pathname)) return;
    const saved = positions.get(location.key);

    if (!saved || saved.size === 0) {
      // New entry: standard top-of-page. The window call is harmless on a
      // container-scrolled page and necessary on every other one.
      window.scrollTo(0, 0);
      requestAnimationFrame(() => window.scrollTo(0, 0));
      return;
    }

    let rafId = 0;
    let tries = 0;
    let cancelled = false;
    const cancel = () => { cancelled = true; if (rafId) cancelAnimationFrame(rafId); };
    const onUserScroll = () => cancel();

    const attempt = () => {
      if (cancelled) return;
      let allDone = true;
      for (const [pane, target] of saved) {
        const maxScroll = maxScrollOf(pane);
        if (readyToRestore({ target, maxScroll, tries })) {
          scrollPaneTo(pane, landingPosition(target, maxScroll));
        } else {
          allDone = false;
        }
      }
      if (allDone || tries >= 90) {
        window.removeEventListener("wheel", onUserScroll);
        window.removeEventListener("touchstart", onUserScroll);
        return;
      }
      tries += 1;
      rafId = requestAnimationFrame(attempt);
    };

    window.addEventListener("wheel", onUserScroll, { passive: true });
    window.addEventListener("touchstart", onUserScroll, { passive: true });
    rafId = requestAnimationFrame(attempt);

    return () => {
      cancel();
      window.removeEventListener("wheel", onUserScroll);
      window.removeEventListener("touchstart", onUserScroll);
    };
  }, [location.key, location.pathname]);

  return null;
}

// Exported for the test that checks which pages this must cover.
export { SELF_MANAGED, readTop };
